import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from '@rails/request.js'
import { tenants_admin_audit_events_path, persons_admin_audit_events_path } from "../routes"

export default class extends Controller {
  static targets = ["subscriberSelect", "tenantSelect", "personSelect"]
  static values = {
    subscriberId: String,
    tenantId: String
  }

  connect() {
    // Store the default option texts from the DOM
    this.allTenantsText = this.tenantSelectTarget.querySelector('option[value=""]')?.textContent || 'Összes bérlő'
    this.allPersonsText = this.personSelectTarget.querySelector('option[value=""]')?.textContent || 'Összes személy'
    
    // Initialize filtered lists if values are already selected
    this.initializeFilters()
  }

  async initializeFilters() {
    // If subscriber is selected, load filtered tenants
    if (this.subscriberIdValue) {
      await this.loadTenants(this.subscriberIdValue)
    }
    
    // If tenant is selected, load filtered persons
    if (this.tenantIdValue) {
      await this.loadPersons(this.tenantIdValue)
    } else if (this.subscriberIdValue) {
      // If only subscriber is selected, load persons from all tenants of that subscriber
      await this.loadPersonsForSubscriber(this.subscriberIdValue)
    }
  }

  async onSubscriberChange(event) {
    const subscriberId = event.target.value
    
    // Reset tenant and person selects
    this.tenantSelectTarget.value = ''
    this.personSelectTarget.innerHTML = `<option value="">${this.allPersonsText}</option>`
    
    if (subscriberId) {
      // Load tenants for this subscriber
      await this.loadTenants(subscriberId)
      // Also update persons list based on subscriber
      await this.loadPersonsForSubscriber(subscriberId)
    } else {
      // Load all tenants
      await this.loadTenants(null)
      // Load all persons
      await this.loadPersons(null)
    }
  }

  async onTenantChange(event) {
    const tenantId = event.target.value
    
    // Reset person select
    this.personSelectTarget.innerHTML = `<option value="">${this.allPersonsText}</option>`
    
    if (tenantId) {
      // Load persons for this tenant
      await this.loadPersons(tenantId)
    } else {
      // Load all persons (with users)
      await this.loadPersons(null)
    }
  }

  async loadTenants(subscriberId) {
    try {
      const url = subscriberId 
        ? tenants_admin_audit_events_path({ subscriber_id: subscriberId })
        : tenants_admin_audit_events_path()
      
      const request = new FetchRequest("get", url, {
        responseKind: "json"
      })
      
      const response = await request.perform()
      
      if (response.ok) {
        const data = await response.json
        this.updateTenantSelect(data.tenants)
      }
    } catch (error) {
      console.error("[audit_events_filter] Error loading tenants:", error)
    }
  }

  async loadPersons(tenantId) {
    try {
      const url = tenantId
        ? persons_admin_audit_events_path({ tenant_id: tenantId })
        : persons_admin_audit_events_path()
      
      const request = new FetchRequest("get", url, {
        responseKind: "json"
      })
      
      const response = await request.perform()
      
      if (response.ok) {
        const data = await response.json
        this.updatePersonSelect(data.persons)
      }
    } catch (error) {
      console.error("[audit_events_filter] Error loading persons:", error)
    }
  }

  async loadPersonsForSubscriber(subscriberId) {
    try {
      const url = persons_admin_audit_events_path({ subscriber_id: subscriberId })
      
      const request = new FetchRequest("get", url, {
        responseKind: "json"
      })
      
      const response = await request.perform()
      
      if (response.ok) {
        const data = await response.json
        this.updatePersonSelect(data.persons)
      }
    } catch (error) {
      console.error("[audit_events_filter] Error loading persons for subscriber:", error)
    }
  }

  updateTenantSelect(tenants) {
    const select = this.tenantSelectTarget
    const currentValue = select.value
    
    // Clear existing options except the first (blank) option
    select.innerHTML = `<option value="">${this.allTenantsText}</option>`
    
    // Add new options
    tenants.forEach(tenant => {
      const option = document.createElement('option')
      option.value = tenant.id
      option.textContent = tenant.name
      if (tenant.id.toString() === currentValue) {
        option.selected = true
      }
      select.appendChild(option)
    })
  }

  updatePersonSelect(persons) {
    const select = this.personSelectTarget
    const currentValue = select.value
    
    // Clear existing options except the first (blank) option
    select.innerHTML = `<option value="">${this.allPersonsText}</option>`
    
    // Add new options
    persons.forEach(person => {
      const option = document.createElement('option')
      option.value = person.id
      option.textContent = person.name
      if (person.id.toString() === currentValue) {
        option.selected = true
      }
      select.appendChild(option)
    })
  }
}

